/**
 * Карточка договора в кабинете контрагента: состав полей — белый список с бэка (внутренних
 * согласующих и комментариев в нём нет), плюс обмен файлами.
 *
 * Скачивание идёт blob'ом с токеном, а не ссылкой: ручка вложения требует `Authorization`.
 * Выбор файла — web-only, как и везде в проекте (нативного файлового пикера нет, см.
 * src/lib/files.ts).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useState } from 'react'
import { Platform } from 'react-native'
import { Text, XStack, YStack } from 'tamagui'

import { api, type CabinetAttachment } from '../../../src/api/client'
import { StateBadge } from '../../../src/features/cabinet/StateBadge'
import { errorText } from '../../../src/features/edo/errorText'
import { useApp } from '../../../src/i18n'
import { downloadWithAuth, pickFileWeb } from '../../../src/lib/files'
import { fmtKb } from '../../../src/lib/format'
import { tone } from '../../../src/theme/tones'
import { Button, Icon, QueryState, SkeletonLines } from '../../../src/ui'
import {
  CabinetBack,
  CabinetShell,
} from '../../../src/features/cabinet/CabinetShell'

/** То же, что принимает бэк по содержимому файла (file-content.ts). */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx'

function Row({ label, value }: { label: string; value?: string | number | null }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <XStack gap={10} alignItems="flex-start">
      <Text fontSize={12} color="$textMuted" width={150}>
        {label}
      </Text>
      <Text fontSize={13} color="$color" flex={1}>
        {String(value)}
      </Text>
    </XStack>
  )
}

export default function CabinetContractScreen() {
  const { t, l } = useApp()
  const router = useRouter()
  const { id } = useLocalSearchParams<{ id: string }>()
  const qc = useQueryClient()
  const [localError, setLocalError] = useState('')

  const q = useQuery({
    queryKey: ['cabinet', 'contract', id],
    queryFn: () => api.cabinetContract(String(id)),
    enabled: !!id,
  })

  const attach = useMutation({
    mutationFn: async () => {
      const file = await pickFileWeb(ACCEPT)
      if (!file) return null
      const form = new FormData()
      form.append('file', file as unknown as Blob)
      return api.cabinetAttach(String(id), form)
    },
    onSuccess: (res) => {
      if (res) void qc.invalidateQueries({ queryKey: ['cabinet', 'contract', id] })
    },
  })

  const download = (a: CabinetAttachment) => {
    setLocalError('')
    void downloadWithAuth(() => api.cabinetAttachment(a.uploadId), a.name).catch((e) =>
      setLocalError(e instanceof Error ? t(errorText(e)) : String(e)),
    )
  }

  const row = q.data
  const error =
    localError || (attach.error instanceof Error ? t(errorText(attach.error)) : '')

  return (
    <CabinetShell section="contracts">
      <CabinetBack to="contracts" label={l('cab.nav.contracts')} />
      <Text fontSize={26} fontWeight="800" color="$color" letterSpacing={-0.4}>
        {row?.title || (row ? `№ ${row.number}` : l('cab.title'))}
      </Text>
      <QueryState q={q}>
        {q.isLoading || !row ? (
          <SkeletonLines lines={4} />
        ) : (
          <YStack gap={18}>
            {/* Состояние — первым: человек открыл договор, чтобы узнать, где тот стоит. */}
            <XStack>
              <StateBadge state={row.state} />
            </XStack>

            <YStack gap={6}>
              <Row label={l('cab.card.number')} value={row.contractNo || row.docNo || `№ ${row.number}`} />
              <Row label={l('cab.card.subject')} value={row.subject} />
              <Row
                label={l('cab.card.amount')}
                value={row.amount !== undefined ? `${row.amount} ${row.currency ?? ''}`.trim() : null}
              />
              <Row label={l('cab.card.created')} value={row.createdAt.slice(0, 10)} />
              <Row label={l('cab.card.signed')} value={row.signedAt?.slice(0, 10) ?? null} />
              <Row label={l('cab.card.counterparty')} value={row.counterpartyName} />
            </YStack>

            <YStack gap={10}>
              <Text fontSize={16} fontWeight="700" color="$color">
                {l('cab.files.title')}
              </Text>

              {row.attachments.length === 0 ? (
                <Text fontSize={13} color="$textMuted">
                  {l('cab.files.empty')}
                </Text>
              ) : (
                <YStack gap={8}>
                  {row.attachments.map((a) => (
                    <XStack
                      key={a.uploadId}
                      backgroundColor="$bgCard"
                      borderWidth={1}
                      borderColor="$borderColor"
                      borderRadius={8}
                      padding={10}
                      gap={10}
                      alignItems="center"
                    >
                      <Icon name="file-text" size={16} color="#64748B" />
                      <YStack flex={1} gap={2}>
                        <Text fontSize={13} color="$color">
                          {a.name}
                        </Text>
                        <Text fontSize={11} color="$textMuted">
                          {fmtKb(a.size)} · {a.createdAt.slice(0, 10)}
                        </Text>
                      </YStack>
                      {/* Скачивание — web-only: нативного файлового слоя в проекте нет. */}
                      {Platform.OS === 'web' && (
                        <Button size="sm" onPress={() => download(a)}>
                          {l('cab.files.download')}
                        </Button>
                      )}
                    </XStack>
                  ))}
                </YStack>
              )}

              {Platform.OS === 'web' && (
                <YStack gap={4}>
                  <Button
                    size="sm"
                    disabled={attach.isPending}
                    onPress={() => attach.mutate()}
                  >
                    {attach.isPending ? l('cab.files.attaching') : l('cab.files.attach')}
                  </Button>
                  <Text fontSize={11} color="$textMuted">
                    {l('cab.files.hint')}
                  </Text>
                </YStack>
              )}

              {!!error && (
                <Text fontSize={13} color={tone('red').text}>
                  {error}
                </Text>
              )}
            </YStack>
          </YStack>
        )}
      </QueryState>
    </CabinetShell>
  )
}
